import React, { useMemo, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Backdrop,
  CircularProgress,
  Typography,
  MenuItem,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import DatePicker from 'src/components/DatePicker';
import {
  useGestionMarcacionApi,
  HoraProcesada,
  OperarioItem,
} from '../hooks/useGestionMarcacionApi';
import { sumarHorasHHMM } from '../utils/marcacionUtils';

interface Props {
  puntosServicio: string[];
  operarios: OperarioItem[];
}

const TIPOS_HORA = [
  { value: '', label: 'Todos' },
  { value: 'NORMA', label: 'Normal' },
  { value: 'HNOC', label: 'Nocturna' },
  { value: 'HNOCM', label: 'Nocturna Mensualeros' },
  { value: 'DOMIN', label: 'Domingo' },
  { value: 'DONOC', label: 'Domingo Noc.' },
  { value: 'FERIA', label: 'Feriado' },
  { value: 'FENOC', label: 'Feriado Noc.' },
];

const RANGO_MAX_DIAS = 7;
const ROWS_PER_PAGE = 25;

const toLocalISODate = (date: Date | undefined): string => {
  if (!date) return '';
  return date.toISOString().slice(0, 10);
};

const diasEntre = (desde: Date | undefined, hasta: Date | undefined): number => {
  if (!desde || !hasta) return 0;
  const ms = hasta.getTime() - desde.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
};

const totalsLabels: Array<{ key: string; label: string }> = [
  { key: 'norma', label: 'Normal' },
  { key: 'hnoc', label: 'Nocturna' },
  { key: 'domin', label: 'Domingo' },
  { key: 'donoc', label: 'Domingo Noc.' },
  { key: 'feria', label: 'Feriado' },
  { key: 'fenoc', label: 'Feriado Noc.' },
  { key: 'cincuenta', label: '50%' },
];

const ProcesadasPanel: React.FC<Props> = ({ puntosServicio, operarios }) => {
  const api = useGestionMarcacionApi();

  // Filtros propios del tab
  const [puntoServicio, setPuntoServicio] = useState<string>('');
  const [fechaInicio, setFechaInicio] = useState<Date | undefined>(new Date());
  const [fechaFin, setFechaFin] = useState<Date | undefined>(new Date());
  const [operarioSelect, setOperarioSelect] = useState<OperarioItem | null>(null);
  const [tipoHora, setTipoHora] = useState<string>('');

  // Resultados
  const [registros, setRegistros] = useState<HoraProcesada[]>([]);
  const [horasTotales, setHorasTotales] = useState<Record<string, string>>({});
  const [yaBusco, setYaBusco] = useState(false);

  // UI
  const [loading, setLoading] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState<string>('');

  // Paginación local
  const [page, setPage] = useState(0);

  const paginados = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return registros.slice(start, start + ROWS_PER_PAGE);
  }, [registros, page]);

  const totalPaginas = Math.max(1, Math.ceil(registros.length / ROWS_PER_PAGE));

  const validar = (): string => {
    if (!fechaInicio || !fechaFin) return 'Debés seleccionar un rango de fechas.';
    if (fechaInicio > fechaFin) return 'La fecha inicio no puede ser mayor a la fecha fin.';
    const dias = diasEntre(fechaInicio, fechaFin);
    if (dias > RANGO_MAX_DIAS) {
      return `El rango no puede superar ${RANGO_MAX_DIAS} días (consulta optimizada).`;
    }
    if (!operarioSelect) {
      return 'Debés indicar un Operario.';
    }
    return '';
  };

  const handleBuscar = async () => {
    const err = validar();
    if (err) {
      setErrorGeneral(err);
      return;
    }
    setLoading(true);
    setErrorGeneral('');
    setPage(0);
    const filtro = {
      fechaInicio: toLocalISODate(fechaInicio),
      fechaFin: toLocalISODate(fechaFin),
      operario: operarioSelect ? `${operarioSelect.nombre} - ${operarioSelect.legajo}` : '',
      tipoHora: tipoHora,
    };
    const res = await api.traerHorasProcesadas(filtro);
    if (res.ok && res.data) {
      // Filtrado por PS en frontend (el backend no lo expone bien si se pasa nombre, solo psID numérico).
      const filtrados = puntoServicio
        ? res.data.info.filter((h) => String(h.NombrePServicio || h.puntoServicio || '').trim() === puntoServicio)
        : res.data.info;
      // Ordenar por fecha ascendente.
      const ordenados = [...filtrados].sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
      setRegistros(ordenados);
      setHorasTotales(res.data.horas);
      setYaBusco(true);
    } else {
      setRegistros([]);
      setHorasTotales({});
      setErrorGeneral(res.error || 'No se pudieron obtener las horas procesadas.');
    }
    setLoading(false);
  };

  const handleLimpiar = () => {
    setPuntoServicio('');
    setFechaInicio(new Date());
    setFechaFin(new Date());
    setOperarioSelect(null);
    setTipoHora('');
    setRegistros([]);
    setHorasTotales({});
    setYaBusco(false);
    setErrorGeneral('');
    setPage(0);
  };

  // Si filtramos en frontend por PS, los totales del backend ya no aplican exactamente:
  // recalculamos los totales mostrados a partir de los registros filtrados.
  const totalesEfectivos = useMemo(() => {
    if (!puntoServicio) return horasTotales;
    const recompute: Record<string, string> = {
      norma: '00:00', hnoc: '00:00', domin: '00:00', donoc: '00:00', feria: '00:00', fenoc: '00:00', cincuenta: '00:00',
    };
    registros.forEach((r) => {
      const t = String(r.tipohora || '').toUpperCase();
      const total = r.total || '00:00';
      if (t === 'NORMA' || t === 'HNORM') recompute.norma = sumarHorasHHMM(recompute.norma, total);
      else if (t === 'HNOCT' || t === 'HNOC' || t === 'HNOCM') recompute.hnoc = sumarHorasHHMM(recompute.hnoc, total);
      else if (t === 'DOMIN') recompute.domin = sumarHorasHHMM(recompute.domin, total);
      else if (t === 'DONOC') recompute.donoc = sumarHorasHHMM(recompute.donoc, total);
      else if (t === 'FERIA') recompute.feria = sumarHorasHHMM(recompute.feria, total);
      else if (t === 'FENOC') recompute.fenoc = sumarHorasHHMM(recompute.fenoc, total);
      else if (t === '50%') recompute.cincuenta = sumarHorasHHMM(recompute.cincuenta, total);
    });
    return recompute;
  }, [puntoServicio, registros, horasTotales]);

  return (
    <>
      <div className="gmv2-card">
        <h3>Filtros</h3>
        <div className="gmv2-filtros-grid">
          <Autocomplete
            options={puntosServicio}
            value={puntoServicio || null}
            onChange={(_e, val) => setPuntoServicio(val || '')}
            getOptionLabel={(opt) => opt}
            renderInput={(params) => (
              <TextField {...params} label="Punto de Servicio" variant="outlined" size="small" />
            )}
          />

          <DatePicker placeholder="Fecha Inicio" value={fechaInicio} setValue={setFechaInicio} />
          <DatePicker placeholder="Fecha Fin" value={fechaFin} setValue={setFechaFin} />

          <Autocomplete
            options={operarios}
            value={operarioSelect}
            onChange={(_e, val) => setOperarioSelect(val as OperarioItem | null)}
            getOptionLabel={(opt: OperarioItem) => `${opt.nombre} - ${opt.legajo}`}
            renderInput={(params) => (
              <TextField {...params} label="Operario" variant="outlined" size="small" />
            )}
          />

          <TextField
            select
            label="Tipo de hora"
            value={tipoHora}
            onChange={(e) => setTipoHora(e.target.value)}
            variant="outlined"
            size="small"
          >
            {TIPOS_HORA.map((opt) => (
              <MenuItem key={opt.value || 'todos'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#888' }}>
          Para optimizar la consulta, el rango máximo es de {RANGO_MAX_DIAS} días y el Operario es obligatorio. El Punto de Servicio es opcional.
        </Typography>

        <div className="gmv2-acciones">
          <Button variant="outlined" onClick={handleLimpiar} disabled={loading} startIcon={<RefreshIcon />}>
            Limpiar
          </Button>
          <Button
            variant="contained"
            onClick={handleBuscar}
            disabled={loading}
            style={{ backgroundColor: '#900533', color: 'white' }}
            startIcon={<SearchIcon />}
          >
            Buscar
          </Button>
        </div>

        {errorGeneral && (
          <Box marginTop={1}>
            <Typography color="error" variant="body2">{errorGeneral}</Typography>
          </Box>
        )}
      </div>

      {yaBusco && (
        <div className="gmv2-card">
          <h3>Totales por tipo de hora</h3>
          <div className="gmv2-chips-cupos">
            {totalsLabels.map(({ key, label }) => (
              <span key={key} className="gmv2-chip-cupo">
                <span className="label">{label}:</span>
                <span>{totalesEfectivos[key] || '00:00'}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {yaBusco && (
        <div className="gmv2-card">
          <h3>Horas procesadas ({registros.length})</h3>

          {registros.length === 0 ? (
            <div className="gmv2-empty">
              No se encontraron horas procesadas. Si gestionaste recientemente, el motor puede tardar en procesarlas.
            </div>
          ) : (
            <>
              {paginados.map((r, idx) => (
                <div key={`${r.cedula}-${r.fecha}-${r.tipohora}-${idx}`} className="gmv2-registro">
                  <div className="gmv2-registro-head">
                    <div>
                      <div className="gmv2-registro-nombre">{r.nombre}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        Legajo {r.legajo} · {r.NombrePServicio || r.puntoServicio}
                      </div>
                    </div>
                    <span className="gmv2-estado supera">{r.tipohora || '-'}</span>
                  </div>

                  <div className="gmv2-registro-meta">
                    <span><strong>Fecha:</strong> {r.fecha}</span>
                    <span><strong>Horario:</strong> {r.horario || '-'}</span>
                    <span><strong>Total:</strong> {r.total || '-'}</span>
                  </div>
                </div>
              ))}

              <div className="gmv2-paginacion">
                <div className="gmv2-paginacion-row">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Anterior
                  </Button>
                  <span className="gmv2-paginacion-info">Página {page + 1} de {totalPaginas}</span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setPage((p) => Math.min(totalPaginas - 1, p + 1))}
                    disabled={page >= totalPaginas - 1}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {!yaBusco && !loading && (
        <div className="gmv2-card gmv2-empty">
          Indicá un Operario y presioná "Buscar" para ver horas procesadas.
        </div>
      )}

      <Backdrop open={loading} style={{ zIndex: 1300, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default ProcesadasPanel;
